import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { routeConfig, VIEW_MODE_MAP, PERMISSION_VIEW_MAP } from "@/config/routeConfig";
import { useRole } from "./use-role";

export const useJumpView = () => {
    const navigate = useNavigate();
    const permission = useSelector((state) => state.Auth.permission);
    const role = useRole();

    // 检查用户是否有某个菜单的权限
    const hasMenuPermission = useCallback((menuCode) => {
        // 超级管理员拥有所有权限
        if (role === 1) {
            return true;
        }
        
        // 如果没有权限代码，表示具有权限
        if (!menuCode) {
            return true;
        }
        
        // 检查权限值是否大于0
        return permission[menuCode] && permission[menuCode] > 0;
    }, [permission, role]);

    // 递归查找第一个有权限的菜单路径
    const findFirstAccessiblePath = useCallback((menuItems) => {
        for (const item of menuItems) {
            // 如果有路径且用户有权限访问
            if (item.path && hasMenuPermission(item.code)) {
                return item.path;
            }
            
            // 如果有子菜单，递归查找
            if (item.children && item.children.length > 0) {
                const childPath = findFirstAccessiblePath(item.children);
                if (childPath) {
                    return childPath;
                }
            }
        }
        return null;
    }, [hasMenuPermission]);

    // 跳转到指定视图模式
    return useCallback((viewMode) => {
        // 获取视图类型
        const viewType = VIEW_MODE_MAP[viewMode];
        if (!viewType) {
            console.error(`Invalid viewMode: ${viewMode}`);
            return;
        }

        // 检查用户是否有该视图的权限
        const viewPermissionKey = PERMISSION_VIEW_MAP[viewType];
        if (!hasMenuPermission(viewPermissionKey)) {
            console.error(`User doesn't have permission for ${viewType} view`);
            return;
        }

        // 获取该视图的路由配置
        const viewRoutes = routeConfig[viewType];
        if (!viewRoutes || viewRoutes.length === 0) {
            console.error(`No routes configured for ${viewType} view`);
            return;
        }

        // 查找第一个有权限的菜单路径
        const firstAccessiblePath = findFirstAccessiblePath(viewRoutes);
        
        if (firstAccessiblePath) {
            // 跳转到第一个有权限的菜单
            navigate(firstAccessiblePath);
        } else {
            // 如果没有找到有权限的菜单，跳转到对应的404页面
            const errorPath = `/${viewType}/404`;
            navigate(errorPath);
        }
    }, [navigate, hasMenuPermission, findFirstAccessiblePath]);
};

/*
使用示例：

import { useJumpView } from '@/hooks/use-jump-view';

const MyComponent = () => {
    const jumpToView = useJumpView();
    
    const handleSwitchToFacility = () => {
        // 跳转到 Facility View (viewMode = 0)
        jumpToView(0);
    };
    
    const handleSwitchToAdmin = () => {
        // 跳转到 Admin View (viewMode = 1)
        jumpToView(1);
    };
    
    const handleSwitchToPortfolio = () => {
        // 跳转到 Portfolio View (viewMode = 2)
        jumpToView(2);
    };
    
    return (
        <div>
            <button onClick={handleSwitchToFacility}>Facility View</button>
            <button onClick={handleSwitchToAdmin}>Admin View</button>
            <button onClick={handleSwitchToPortfolio}>Portfolio View</button>
        </div>
    );
};
*/